import axios from "axios";
import {
  AccountRegisterServicePayload,
  AccountRegisterServiceResponse,
  EmailVerifcationServiceResponse,
  EmailVerificaitonServicePayload,
  LoginServicePayload,
  LoginServiceResponse,
  LoginWithOTPServicePayload,
  LoginWithOTPServiceResponse,
  ValidateOTPServicePayload,
  ValidateOTPServiceResponse,
  GetMyTeamResponse,
  UpdateAccountServicePayload,
  UpdateAccountServiceResponse,
  ClearSsoSessionServiceResponse,
  AccountLogoutServiceResponse,
  UserImageServicePayload,
  UserImageServiceParams,
  MyProfileResponse,
  ProfessionalDescriptionItem,
} from "interfaces";
import instance from "lib/axiosInterceptor";
import { baseUrl } from "utils/baseUrl";

// axios is used here instead of instance to avoid interceptor effects on the login/register requests
export async function loginService(
  payload: LoginServicePayload,
): Promise<LoginServiceResponse> {
  const response = await axios.post<LoginServiceResponse>(
    `${baseUrl}/Account/login`,
    payload,
    { withCredentials: true },
  );
  return response.data;
}

export async function loginWithOTPService(
  payload: LoginWithOTPServicePayload,
): Promise<LoginWithOTPServiceResponse> {
  const response = await axios.post<LoginWithOTPServiceResponse>(
    `${baseUrl}/Account/login/WithOTP`,
    payload,
  );
  return response.data;
}

export async function emailVerificationService(
  payload: EmailVerificaitonServicePayload,
): Promise<EmailVerifcationServiceResponse> {
  const response = await axios.post<EmailVerifcationServiceResponse>(
    `${baseUrl}/Account/login/verify`,
    payload,
  );
  return response.data;
}

export async function validateOTPService(
  payload: ValidateOTPServicePayload,
): Promise<ValidateOTPServiceResponse> {
  const response = await axios.post<ValidateOTPServiceResponse>(
    `${baseUrl}/Account/login/ValidateOTP`,
    payload,
  );
  return response.data;
}

export async function accountRegisterService(
  payload: AccountRegisterServicePayload,
): Promise<AccountRegisterServiceResponse> {
  const response = await axios.post<AccountRegisterServiceResponse>(
    `${baseUrl}/Account/Register`,
    payload,
  );
  return response.data;
}

export async function clearSsoSessionService(): Promise<
  ClearSsoSessionServiceResponse
> {
  const response = await instance.post<ClearSsoSessionServiceResponse>(
    "/Account/Sso/Session/Clear",
  );
  return response.data;
}

export async function accountLogoutService(): Promise<
  AccountLogoutServiceResponse
> {
  const response =
    await instance.post<AccountLogoutServiceResponse>("/Account/Logout");
  return response.data;
}

export async function getProfilePictureService(): Promise<Blob | string> {
  const response = await instance.get(`/Account/ProfilePic`, {
    responseType: "arraybuffer",
  });

  const buffer: ArrayBuffer = response.data;
  const decoder = new TextDecoder();
  const header = decoder.decode(new Uint8Array(buffer).slice(0, 200));
  const trimmed = header.trimStart();

  if (trimmed.startsWith("<svg") || trimmed.startsWith("<?xml")) {
    const svgText = decoder.decode(new Uint8Array(buffer));
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
  }

  const contentType = String(response.headers?.["content-type"] || "image/jpeg");
  return new Blob([buffer], {
    type: contentType.startsWith("image/") ? contentType : "image/jpeg",
  });
}

export async function getMemberImageService(memberId: string): Promise<string> {
  const response = await instance.get(`/Account/UserImage/${memberId}`, {
    responseType: "arraybuffer",
  });

  const buffer: ArrayBuffer = response.data;
  if (!buffer.byteLength) return "";

  const decoder = new TextDecoder();
  const header = decoder.decode(new Uint8Array(buffer).slice(0, 200));
  const trimmed = header.trimStart();

  if (trimmed.startsWith("<svg") || trimmed.startsWith("<?xml")) {
    const svgText = decoder.decode(new Uint8Array(buffer));
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
  }

  const contentType = String(response.headers?.["content-type"] || "image/jpeg");
  const mimeType = contentType.startsWith("image/")
    ? contentType
    : "image/jpeg";
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return `data:${mimeType};base64,${btoa(binary)}`;
}

export async function getUserImageService(
  payload: UserImageServicePayload,
): Promise<Blob | string> {
  const { id, params } = payload;

  const queryParams: UserImageServiceParams = {
    ...params,
  };

  // Remove undefined values
  Object.keys(queryParams).forEach((key) => {
    if (queryParams[key as keyof UserImageServiceParams] === undefined) {
      delete queryParams[key as keyof UserImageServiceParams];
    }
  });

  const response = await instance.get(`/Account/UserImage/${id}`, {
    params: queryParams,
    responseType: "arraybuffer",
  });

  const buffer: ArrayBuffer = response.data;
  const decoder = new TextDecoder();
  const header = decoder.decode(new Uint8Array(buffer).slice(0, 200));
  const trimmed = header.trimStart();

  if (trimmed.startsWith("<svg") || trimmed.startsWith("<?xml")) {
    const svgText = decoder.decode(new Uint8Array(buffer));
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
  }

  const contentType = String(response.headers?.["content-type"] || "image/jpeg");
  return new Blob([buffer], {
    type: contentType.startsWith("image/") ? contentType : "image/jpeg",
  });
}

export async function updateProfilePictureService(file: File) {
  const { fullName, phone } = JSON.parse(localStorage.getItem("user") || "{}");

  const formData = new FormData();
  formData.append("fullName", fullName || "");
  formData.append("phone", phone || "");
  formData.append("picture", file);

  const response = await instance.post("/Account/Update", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
}

export async function updateAccountService(
  payload: UpdateAccountServicePayload,
): Promise<UpdateAccountServiceResponse> {
  const response = await instance.post<UpdateAccountServiceResponse>(
    "/Account/Update",
    payload,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return response.data;
}

export async function getMyProfileService(): Promise<MyProfileResponse> {
  const response = await instance.get<MyProfileResponse>("/Account/MyProfile");
  return response.data;
}

export async function getProfessionalDescriptionService(): Promise<
  ProfessionalDescriptionItem[]
> {
  const response = await instance.get<ProfessionalDescriptionItem[]>(
    "/Account/GetProfessionalDescription",
  );
  return response.data;
}

export async function getMyTeamService(): Promise<GetMyTeamResponse> {
  const response = await instance.get<GetMyTeamResponse>("/MyTeam");
  return response.data;
}

export async function getTeamMembersService(): Promise<any[]> {
  const response = await instance.get<any[]>("/Team/Members");
  return response.data;
}
